import { useState, useMemo } from 'react';
import {
  Grid,
  Box,
  Typography,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Collapse,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  OpenInNew,
  CalendarToday,
  Assignment,
  Person
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import {userOptions} from "../../../user";
import { DealTypesMapping, RolesTitleSingular } from '../../../dictionaries';

// Styled компоненты
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transition: 'background-color 0.2s ease',
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
  cursor: 'pointer',
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  borderRadius: '6px',
  fontWeight: 500,
  fontSize: '12px',
  height: '24px',
  '& .MuiChip-label': {
    padding: '0 10px',
  },
}));

const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.text.primary,
  textDecoration: 'none',
  fontWeight: 400,
  '&:hover': {
    color: theme.palette.primary.main,
    textDecoration: 'underline',
  },
  transition: 'color 0.2s ease',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
}));

// Компонент строки сделки
const DealRow = ({ deal, index, openTab }) => {
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleRowClick = () => {
    if (isMobile) {
      setExpanded(!expanded);
    }
  };

  const handleLinkClick = (e, href) => {
    e.stopPropagation();
    window.open(href, openTab ? '_blank' : '_self');
  };

  const getTypeColor = (type) => {
    const colors = {
      'sale': 'success',
      'purchase': 'info',
      'rent': 'warning',
      'exchange': 'secondary',
    };
    return colors[type] || 'default';
  };

  const formatDate = (date) => {
    return date;
  };

  return (
    <>
      <StyledTableRow onClick={handleRowClick}>
        <TableCell sx={{ minWidth: '100px', padding: '4px 8px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <CalendarToday sx={{ fontSize: '14px', color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {formatDate(deal.createdAt)}
            </Typography>
          </Box>
        </TableCell>
        <TableCell sx={{ minWidth: '200px', padding: '12px 8px' }}>
          <StyledLink
            href="#"
            onClick={(e) => handleLinkClick(e, `/deal?id=${deal.id}`)}
          >
            <Assignment sx={{ fontSize: '14px' }} />
            {deal.title}
          </StyledLink>
        </TableCell>
        <TableCell sx={{ padding: '12px 8px' }}>
          {DealTypesMapping[deal.type] || deal.type}
        </TableCell>
      </StyledTableRow>

      {isMobile && (
        <TableRow>
          <TableCell colSpan={6} sx={{ padding: 0 }}>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ padding: '16px 24px', backgroundColor: 'action.hover' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Дата создания
                    </Typography>
                    <Typography variant="body2">
                      {new Date(deal.createdAt).toLocaleString('ru-RU')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Название сделки
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {deal.title}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Тип сделки
                    </Typography>
                    <Box mt={0.5}>
                      <StyledChip
                        label={DealTypesMapping[deal.type] || deal.type}
                        color={getTypeColor(deal.type)}
                        size="small"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <StyledLink
                      href="#"
                      onClick={(e) => handleLinkClick(e, `/deal?id=${deal.id}`)}
                      sx={{ color: 'primary.main' }}
                    >
                      Открыть сделку
                      <OpenInNew sx={{ fontSize: '14px' }} />
                    </StyledLink>
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

// Основной компонент
export function SubjectDealsList({ data }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  // Мемоизация данных для оптимизации
  const deals = useMemo(() => data || [], [data]);

  if (!deals.length) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 4,
          textAlign: 'center',
          backgroundColor: 'transparent',
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 2
        }}
      >
        <Typography variant="body1" color="text.secondary">
          Сделки отсутствуют
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography
        component="div"
        sx={{
          mb: 3,
          fontWeight: 400,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        Участвует в сделках
        <Chip
          label={deals.length}
          size="small"
          color="primary"
          sx={{ ml: 1 }}
        />
      </Typography>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Table size="medium">
          {!isMobile && (
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 600 }}>Дата</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Название</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Тип</TableCell>
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {deals.map((deal, index) => (
              <DealRow
                key={deal.id || index}
                deal={deal}
                index={index}
                openTab={userOptions.openLinkCurrentTab}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {deals.length > 10 && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Показано {deals.length} сделок
          </Typography>
        </Box>
      )}
    </Box>
  );
}